import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { SkeletonRows } from 'components/Skeleton'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import { unlinkedToast } from 'features/taskLinks/UndoUnlinkToast'
import taskLinksAPI from 'features/taskLinks/taskLinksAPI'
import useTaskRelated from 'features/taskLinks/useTaskRelated'
import LinkBadge from 'features/taskLinks/LinkBadge'
import SuggestionRow from 'features/taskLinks/SuggestionRow'
import { LINKS_EXPLAINER, SUGGESTION_HELP, errorMessage } from 'features/taskLinks/linkCopy'
import { RAG } from 'features/dashboard/charts/scoreColour'
import TasksRemedialsExplainer from 'features/taskManagement/TasksRemedialsExplainer'
import { BORDER, INK, LINK, MUTED, TextButton } from 'features/actions/workspace/ui'

const SEVERITY_LABEL = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' }
const REMEDIAL_STATUS = {
  pending_review: 'Awaiting review',
  open: 'Open',
  converted: 'Task planned',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
}

const Severity = ({ level }) => {
  const rag = RAG[level]
  if (!rag) return null
  return <Sev style={{ color: rag.fg, background: rag.bg }}>{SEVERITY_LABEL[level]}</Sev>
}

const TaskRelatedDocuments = ({ taskId, bpId, bpName, canManage }) => {
  const [busy, setBusy] = useState(null)
  const [insightFile, setInsightFile] = useState(null)
  const { result, refresh: load } = useTaskRelated(taskId)

  const data = useMemo(() => {
    if (!result) return null
    if (result.failed) return { documents: [], remedials: [], suggestions: [], failed: true }
    return result.data || { documents: [], remedials: [], suggestions: [] }
  }, [result])

  const act = async (key, fn, message) => {
    setBusy(key)
    try {
      await fn()
      toast.success(message)
      load()
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  const openDocument = (fileId, name) => {
    if (!bpId || !fileId) return
    setInsightFile({ id: fileId, displayName: name, buildingPassportId: bpId, buildingPassportName: bpName })
  }

  const manage = !!canManage && !!data?.canManage

  return (
    <Wrap data-tour='task-related-documents' aria-labelledby={`task-related-${taskId}`}>
      <Heading id={`task-related-${taskId}`}>
        <DescriptionOutlinedIcon aria-hidden='true' />
        Related documents
      </Heading>
      <Intro>Documents this task relates to, and the remedials from them that it deals with. {LINKS_EXPLAINER}</Intro>
      <TasksRemedialsExplainer />

      {data === null && <SkeletonRows rows={3} gap={10} widths={['100%']} />}

      {data?.failed && (
        <Muted>We could not load related documents. <Retry type='button' onClick={load}>Try again</Retry></Muted>
      )}

      {data && !data.failed && !data.documents.length && !data.remedials.length && !data.suggestions.length && (
        <Muted>
          No documents are linked to this task yet. You can link one from a document's Guru Insight, or attach it below under Reference documents.
        </Muted>
      )}

      {data && data.documents.length > 0 && (
        <Group>
          {data.documents.map(doc => (
            <Row key={doc.fileId}>
              <DocButton type='button' onClick={() => openDocument(doc.fileId, doc.name)} aria-label={`Open ${doc.name} in Guru Insight`}>
                {doc.name}
              </DocButton>
              <Reason>{doc.reasons.join('. ')}</Reason>
            </Row>
          ))}
        </Group>
      )}

      {data && data.remedials.length > 0 && (
        <Group>
          <SubHeading>Remedials this task deals with</SubHeading>
          {data.remedials.map(rem => (
            <Row key={rem.id}>
              <Line>
                <Severity level={rem.severity} />
                <RemLink to={`/actions/${rem.id}`}>{rem.title}</RemLink>
                <LinkBadge relation={rem.relation} source={rem.source} />
                <Reason>{REMEDIAL_STATUS[rem.status] || ''}</Reason>
                {manage && (
                  <Quiet
                    type='button'
                    disabled={busy === rem.id}
                    aria-label={`Unlink ${rem.title} from this task`}
                    onClick={() => act(rem.id, () => taskLinksAPI.unlink(rem.id, taskId), unlinkedToast({ text: 'Unlinked. We will not suggest it again for this task.', actionId: rem.id, taskId, relation: rem.relation, onRestored: load }))}
                  >
                    Unlink
                  </Quiet>
                )}
              </Line>
              {rem.sourceFileName && <Reason>From {rem.sourceFileName}</Reason>}
            </Row>
          ))}
        </Group>
      )}

      {data && data.suggestions.length > 0 && (
        <Group>
          <SubHeading>This task may also deal with</SubHeading>
          <Muted>{SUGGESTION_HELP}</Muted>
          {data.suggestions.map(s => (
            <SuggestionRow
              key={s.matchId}
              title={s.remedial.title}
              to={`/actions/${s.remedial.id}`}
              context={[SEVERITY_LABEL[s.remedial.severity] && `${SEVERITY_LABEL[s.remedial.severity]} remedial`, s.remedial.sourceFileName && `from ${s.remedial.sourceFileName}`].filter(Boolean).join(' ')}
              reasons={s.reasons}
              canManage={manage}
              busy={busy === s.matchId}
              onLink={relation => act(s.matchId, () => taskLinksAPI.link(s.remedial.id, taskId, relation), 'Remedial linked to this task.')}
              onDismiss={() => act(s.matchId, () => taskLinksAPI.dismiss(s.matchId), 'Got it. We will not suggest it again.')}
            />
          ))}
        </Group>
      )}

      {insightFile && (
        <GuruInsightModal
          open
          onClose={() => { setInsightFile(null); load() }}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}
    </Wrap>
  )
}

export default TaskRelatedDocuments

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Heading = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
  svg { font-size: 20px; color: ${MUTED}; }
`

const Intro = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${MUTED};
`

const Muted = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${MUTED};
`

const Retry = styled(TextButton)`
  min-height: 0;
  padding: 0;
  font-size: 14px;
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 10px;
  border-top: 1px solid ${BORDER};
`

const SubHeading = styled.h4`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
`

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
`

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
`

const DocButton = styled.button`
  align-self: flex-start;
  min-height: 40px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  color: ${LINK};
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  overflow-wrap: anywhere;
  &:focus-visible { outline: 2px solid ${LINK}; outline-offset: 2px; }
`

const RemLink = styled(Link)`
  font-size: 15px;
  font-weight: 600;
  color: ${LINK};
  text-decoration: underline;
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
`

const Reason = styled.span`
  font-size: 14px;
  color: ${MUTED};
`

const Sev = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
`

const Quiet = styled(TextButton)`
  font-size: 14px;
  color: ${MUTED};
`
